import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { AppDateTimePickerComponent } from 'src/app/core/components/app-date-time-picker/app-date-time-picker.component';
import { NoNegativeInputDirective } from 'src/app/core/directives/noNegativeInput.directive';
import { MaterialModule } from 'src/app/core/material.module';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { AppService } from 'src/app/core/services/app.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { StorageBrowser } from 'src/app/core/services/storage.service';
import { TestEventsService } from 'src/app/core/services/test-event.service';
import { UserService } from 'src/app/core/services/user.service';
import { DevicesSelectorComponent } from 'src/app/features/devices/components/devices-selector/devices-selector.component';
import { handleDeviceAssignmentError } from 'src/app/features/devices/helpers/device-error.helper';
import { TestRoomSelectComponent } from 'src/app/features/test-rooms/components/test-rooms-select/test-rooms-select.component';
import {
  ALL_TEST_LOCATION_OPTIONS,
  ALL_TEST_TYPES_OPTIONS,
  BOOLEAN_CHOOSE,
  MAX_TESTS_PER_OPENING,
  MAX_TESTS_PER_OPENING_ERROR,
  RolesEnum,
} from 'src/app/features/tests/features/constants';
import {
  calculateAge,
  getNextScheduleDateTime,
} from 'src/app/features/tests/features/services/helper';
import { handleTestsPerOpeningError } from 'src/app/features/tests/features/helpers/tests-per-opening-error.helper';
import { TestRecordsListService } from 'src/app/features/tests/features/services/test-records-list.service';
import { UserManagementService } from 'src/app/features/user-management/services/user-management.service';
import {
  ReportTemplate,
  isTitrationTemplate,
  TITRATION_TYPE_OPTIONS,
} from 'src/app/features/working-area/components/working-area2/types/signal-types';


type Patient = {
  name: string;
  id: string;
  age: number;
  testsCount: number | null;
  consumedTestsCount: number | null;
};

@Component({
  imports: [
    CommonModule,
    MaterialModule,
    DevicesSelectorComponent,
    AppDateTimePickerComponent,
    ReactiveFormsModule,
    NoNegativeInputDirective,
    TestRoomSelectComponent,
  ],
  selector: 'app-new-test-dialog',
  templateUrl: './new-test-dialog.component.html',
  styleUrls: ['./new-test-dialog.component.scss'],
})
export class NewTestDialogComponent {
  loading = false;
  selectedDevice: any = null;
  currUser: any;
  currDate = moment().add(10, 'minutes').toDate();
  boolChoose = BOOLEAN_CHOOSE;

  technicians: any[];
  allLocationOptions = ALL_TEST_LOCATION_OPTIONS;
  allTypeOptions = ALL_TEST_TYPES_OPTIONS;
  titrationTypeOptions = TITRATION_TYPE_OPTIONS;
  maxTestsPerOpening = MAX_TESTS_PER_OPENING;
  maxTestsPerOpeningError = MAX_TESTS_PER_OPENING_ERROR;

  readonly detailsTab = 0;
  readonly deviceTab = 1;
  selectedTab = this.detailsTab;

  /** An MSLT follows on from an existing test, so it is not given a device step. */
  public get hasDeviceStep(): boolean {
    return this.props?.command !== 'new-mslt' && !this.isDirectPatient;
  }

  /**
   * A Direct patient's tests are done at home: Dormo Direct ships and assigns
   * the device, so the lab picks no device or room. They may have several tests.
   */
  public get isDirectPatient(): boolean {
    return this.data?.patientType === 'DIRECT';
  }

  /** Only a titration (a split night included) is run on a particular device, so only it asks which. */
  public get isTitration(): boolean {
    return isTitrationTemplate(this.testForm.get('testType')?.value);
  }
  public testForm = new FormGroup({
    scheduleDateTime: new FormControl('', [
      Validators.minLength(0),
      Validators.maxLength(256),
    ]),
    technicianId: new FormControl(null, [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    titrationType: new FormControl(''),
    testType: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    location: new FormControl('Lab', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    roomId: new FormControl(undefined, [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    testsCount: new FormControl(1, [
      Validators.min(0),
      Validators.max(MAX_TESTS_PER_OPENING),
      Validators.maxLength(20),
    ]),
    shouldIncludeVideoFeed: new FormControl(false, [
      Validators.min(0),
      Validators.maxLength(20),
    ]),
  });
  props: any;

  constructor(
    public ability: AbilityService,
    public storageBrowser: StorageBrowser,
    private dialogRef: MatDialogRef<NewTestDialogComponent>,
    private userService: UserManagementService,
    private _userService: UserService,
    private testService: TestRecordsListService,
    private testEventsService: TestEventsService,
    private appService: AppService,
    private notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.props = structuredClone(this.data);
    this.data = structuredClone(this.data.patient);
    this.getAllTechnicians();
  }

  onClose(): void {
    this.dialogRef.close();
  }
  ngOnInit() {
    this.currUser = this._userService.currentUser.getValue();

    const scheduleDateTime =  getNextScheduleDateTime();
    this.testForm.patchValue({
      scheduleDateTime,
    });
    if ([RolesEnum.Technician].includes(this.currUser.role)) {
      this.testForm.patchValue({ technicianId: this.currUser.id });
    }
    if (this.isDirectPatient) {
      this.testForm.patchValue({ location: 'Home' });
    }

    if (this.props.command === 'new-mslt') {
      this.testForm.patchValue({
        testType: ReportTemplate.MSLT,
        testsCount: 5,
        roomId: this.props?.test?.room?.id,
        technicianId: this.props?.test?.technicianId ? Number(this.props?.test?.technicianId) : '',
        scheduleDateTime: getNextScheduleDateTime(this.props?.test?.scheduleDateTime),
      });
      this.testForm.get('testsCount')?.disable({ emitEvent: false });
      this.testForm.get('testType')?.disable({ emitEvent: false });
    }

    this.testForm.get('testType')?.valueChanges.subscribe((testType) => {
      const testsCountControl = this.testForm.get('testsCount');

      if (!testsCountControl) return;

      if (testType === ReportTemplate.MSLT) {
        testsCountControl.setValue(6, { emitEvent: false });
        testsCountControl.disable({ emitEvent: false });
      } else {
        testsCountControl.enable({ emitEvent: false });
        testsCountControl.setValue(1, { emitEvent: false });
      }
    });
  }

  ageInfo() {
    if (!this.data.birthdayDate) {
      return '';
    }
    const age = calculateAge(this.data.birthdayDate);
    return age >= 18 ? '- Adult' : '- Child';
  }

  save(): void {
    const value = this.testForm.value;
    if (!value.scheduleDateTime) {
      console.warn('Missing test date');
      return;
    }

    const payload = {
      name: `Test for ${this.data?.name || 'Unknown'}`, // Or allow input
      patientId: this.data?.id,
      deviceId: this.selectedDevice?.id,
      location: value?.location,
      testsCount: value?.testsCount,
      testType: value?.testType,
      // Cleared when the study is not a titration, so a type left behind by an
      // earlier choice cannot name a report the study is not.
      titrationType: this.isTitration ? value?.titrationType || null : null,
      roomId: value?.roomId,
      shouldIncludeVideoFeed: value?.shouldIncludeVideoFeed || false,
      technicianId:
        Number(value.technicianId) > 0 ? Number(value.technicianId) : null,
      scheduleDateTime: value.scheduleDateTime ? value.scheduleDateTime : null,
      command: this.props.command,
      testId: this.props.test?.id || null,
    };

    this.loading = true;
    this.testService.createOrUpdate(payload).subscribe({
      next: (res) => {
        this.loading = false;
        this.testEventsService.emitNewTestCreated(res);
        if (payload.deviceId) {
          this.appService.eventEmitter.emit('DEVICE:UPDATED_STATUS');
        }
        this.dialogRef.close(res);
      },
      error: (err) => {
        this.loading = false;
        if (handleDeviceAssignmentError(err, this.notificationService)) {
          return;
        }
        if (handleTestsPerOpeningError(err, this.notificationService)) {
          return;
        }
        this.notificationService.error('Failed to create test');
        console.error('❌ Failed to create test:', err);
        console.log('🔎 Backend error details:', err?.error?.errors);
      },
    });
  }

  public getAllTechnicians(): void {
    const technicians: any[] = [];
    let currentPage = 1;
    const itemsPerPage = 100; // Use a higher limit if the API supports it

    const fetchPage = () => {
      const options = {
        page: currentPage,
        limit: itemsPerPage,
        role: RolesEnum.Technician,
        is_deleted: false,
      };

      this.userService.getUserList(options).subscribe(
        (resp) => {
          if (resp?.items?.length) {
            technicians.push(...resp.items);

            if (currentPage < resp.meta.totalPages) {
              currentPage++;
              fetchPage(); // Fetch next page
            } else {
              this.technicians = technicians;
            }
          }
        },
        (error) => {
          console.error('Error fetching technicians:', error);
        },
      );
    };

    // Start fetching
    fetchPage();
  }

  /**
   * Test Type is required, so the device step stays shut until the details are
   * complete; trying to move on early marks the fields so the missing ones show.
   */
  goToDeviceTab(): void {
    if (this.testForm.invalid) {
      this.testForm.markAllAsTouched();
      return;
    }
    this.selectedTab = this.deviceTab;
  }

  onDeviceSelected(event: any): void {
    if (event?.valid && event?.value) {
      this.selectedDevice = event.value;
    }
  }

  onTestRoomChange(value: string) {
    this.testForm.get('roomId')?.setValue(value);
  }
}
